import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createTool,
  deleteTool,
  fetchToolById,
  fetchTools,
  fetchToolsName,
  updateTool,
  updateToolStatus,
} from "./toolsAPI";

export const getToolsName = createAsyncThunk(
  "tools/getName",
  async (assistantId, { rejectWithValue }) => {
    try {
      const res = await fetchToolsName(assistantId);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch tools");
    }
  },
);

export const getTools = createAsyncThunk(
  "tools/getAll",
  async (assistantId, { rejectWithValue }) => {
    try {
      return await fetchTools(assistantId);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch tools");
    }
  },
);

export const getToolById = createAsyncThunk(
  "tools/getById",
  async (toolId, { rejectWithValue }) => {
    try {
      return await fetchToolById(toolId);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch tool");
    }
  },
);

export const addTool = createAsyncThunk(
  "tools/save",
  async ({ assistantId, toolData }, { rejectWithValue }) => {
    try {
      return await createTool(assistantId, toolData);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to save tool");
    }
  },
);

export const editTool = createAsyncThunk(
  "tools/update",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      return await updateTool(uuid, data);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update tool");
    }
  },
);

export const editToolStatus = createAsyncThunk(
  "tools/updateStatus",
  async (payload, { rejectWithValue }) => {
    try {
      return await updateToolStatus(payload);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update tool status",
      );
    }
  },
);

export const removeTool = createAsyncThunk(
  "tools/remove",
  async (uuid, { rejectWithValue }) => {
    try {
      await deleteTool(uuid);
      return uuid;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const initialState = {
  items: [],
  currentTool: null,
  loading: false,
  error: null,
  success: false,
};

const toolsSlice = createSlice({
  name: "tools",
  initialState,
  reducers: {
    resetToolState: (state) => {
      state.currentTool = null;
      state.error = null;
      state.success = false;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getToolsName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getToolsName.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
      })
      .addCase(getToolsName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getTools.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTools.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
      })
      .addCase(getTools.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getToolById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentTool = null;
      })
      .addCase(getToolById.fulfilled, (state, action) => {
        state.loading = false;
        const toolData = action.payload?.data || action.payload;
        state.currentTool = toolData;
      })
      .addCase(getToolById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentTool = null;
      })
      .addCase(addTool.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addTool.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const savedTool = action.payload?.data || action.payload;
        if (savedTool?.uuid) {
          const index = state.items.findIndex(
            (tool) => tool.uuid === savedTool.uuid,
          );
          if (index >= 0) {
            state.items[index] = savedTool;
          } else {
            state.items.push(savedTool);
          }
          state.currentTool = savedTool;
        }
      })
      .addCase(addTool.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(editTool.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(editTool.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updatedTool = action.payload?.data || action.payload;
        if (updatedTool?.uuid) {
          state.currentTool = updatedTool;
          const index = state.items.findIndex(
            (tool) => tool.uuid === updatedTool.uuid,
          );
          if (index >= 0) {
            state.items[index] = updatedTool;
          } else {
            state.items.push(updatedTool);
          }
        }
      })
      .addCase(editTool.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(editToolStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(editToolStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updates = action.meta.arg;

        updates.forEach(({ uuid, isEnabled }) => {
          const index = state.items.findIndex((tool) => tool.uuid === uuid);
          if (index >= 0) {
            state.items[index].isEnabled = isEnabled;
          }
        });
      })
      .addCase(editToolStatus.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(removeTool.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeTool.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(
          (tool) => tool.uuid !== action.payload,
        );
      })
      .addCase(removeTool.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetToolState } = toolsSlice.actions;
export default toolsSlice.reducer;
